/**
 * Banking Details Form Component
 * Fourth step of enhanced manufacturer registration
 */

import React, { useState, useEffect } from 'react';
import { Button, Input } from '../ui';
import { BankingDetails, registrationService } from '../../services/registration.service';

interface BankingDetailsFormProps {
  data?: BankingDetails;
  onSave: (data: BankingDetails) => Promise<void>;
  onNext: () => void;
  onPrevious: () => void;
  saving: boolean;
}

export function BankingDetailsForm({ data, onSave, onNext, onPrevious, saving }: BankingDetailsFormProps) {
  const [formData, setFormData] = useState<BankingDetails>({
    bankName: '',
    accountHolderName: '',
    accountNumber: '',
    ifscCode: '',
    gstApplicability: 'CGST_SGST',
    preferredCurrency: 'INR'
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [validatingIFSC, setValidatingIFSC] = useState(false);
  const [ifscValidation, setIfscValidation] = useState<{ status: string; bankInfo?: any }>({ status: 'pending' });

  useEffect(() => {
    if (data) {
      setFormData(data);
    }
  }, [data]);

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.bankName.trim()) {
      newErrors.bankName = 'Bank name is required';
    }

    if (!formData.accountHolderName.trim()) {
      newErrors.accountHolderName = 'Account holder name is required';
    }

    if (!formData.accountNumber.trim()) {
      newErrors.accountNumber = 'Account number is required';
    }

    if (!formData.ifscCode.trim()) {
      newErrors.ifscCode = 'IFSC code is required';
    } else if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(formData.ifscCode)) {
      newErrors.ifscCode = 'Invalid IFSC code format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleValidateIFSC = async () => {
    if (!formData.ifscCode.trim()) return;

    setValidatingIFSC(true);
    try {
      const result = await registrationService.validateIFSC(formData.ifscCode);
      setIfscValidation({
        status: result.status,
        bankInfo: result.data?.bankInfo
      });

      // Auto-fill bank name if validation successful
      if (result.data?.bankInfo?.bankName && !formData.bankName) {
        setFormData(prev => ({
          ...prev,
          bankName: result.data.bankInfo.bankName
        }));
      }
    } catch (error) {
      console.error('IFSC validation error:', error);
    } finally {
      setValidatingIFSC(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSave(formData);
      onNext();
    } catch (error) {
      // Error handling is done in parent component
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* IFSC Code */}
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            IFSC Code *
          </label>
          <div className="flex gap-2">
            <Input
              value={formData.ifscCode}
              onChange={(e) => setFormData(prev => ({ ...prev, ifscCode: e.target.value.toUpperCase() }))}
              placeholder="HDFC0001234"
              maxLength={11}
              error={errors.ifscCode}
              className="flex-1"
            />
            <Button
              type="button"
              variant="outline"
              onClick={handleValidateIFSC}
              disabled={validatingIFSC || !formData.ifscCode.trim()}
              className="px-4"
            >
              {validatingIFSC ? 'Validating...' : 'Validate'}
            </Button>
          </div>
          {ifscValidation.status !== 'pending' && (
            <div className={`mt-1 text-sm ${
              ifscValidation.status === 'verified' ? 'text-green-600' : 
              ifscValidation.status === 'failed' ? 'text-red-600' : 'text-yellow-600'
            }`}>
              {ifscValidation.status === 'verified' && 'IFSC Verified ✓'}
              {ifscValidation.status === 'failed' && 'IFSC Validation Failed ✗'}
              {ifscValidation.status === 'manual_review_required' && 'Manual Review Required'}
              {ifscValidation.bankInfo && (
                <div className="text-gray-600 text-xs mt-1">
                  Bank: {ifscValidation.bankInfo.bankName}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Bank Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Bank Name *
          </label>
          <Input
            value={formData.bankName}
            onChange={(e) => setFormData(prev => ({ ...prev, bankName: e.target.value }))}
            placeholder="Bank name"
            error={errors.bankName}
          />
        </div>

        {/* Account Holder Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Holder Name *
          </label>
          <Input
            value={formData.accountHolderName}
            onChange={(e) => setFormData(prev => ({ ...prev, accountHolderName: e.target.value }))}
            placeholder="As per bank records"
            error={errors.accountHolderName}
          />
        </div>

        {/* Account Number */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Account Number *
          </label>
          <Input
            value={formData.accountNumber}
            onChange={(e) => setFormData(prev => ({ ...prev, accountNumber: e.target.value }))}
            placeholder="Bank account number"
            error={errors.accountNumber}
          />
        </div>

        {/* GST Applicability */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            GST Applicability *
          </label>
          <select
            value={formData.gstApplicability}
            onChange={(e) => setFormData(prev => ({ ...prev, gstApplicability: e.target.value as any }))}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-gray-900 bg-white"
            style={{ color: '#111827' }}
          >
            <option value="CGST_SGST">CGST + SGST (Same State)</option>
            <option value="IGST">IGST (Inter-State)</option>
          </select>
        </div>

        {/* Preferred Currency */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Preferred Currency
          </label>
          <Input
            value={formData.preferredCurrency}
            onChange={(e) => setFormData(prev => ({ ...prev, preferredCurrency: e.target.value }))}
            disabled
          />
        </div>
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex">
          <svg className="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Banking Information</h3>
            <div className="text-sm text-blue-700 mt-1">
              <p>• This account will be used for refunds and credit settlements</p>
              <p>• Please ensure the account holder name matches your company registration</p>
              <p>• You may be asked to upload a cancelled cheque for verification</p>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="outline"
          onClick={onPrevious}
        >
          Previous
        </Button>
        <Button
          type="submit"
          disabled={saving}
          className="px-6 py-2"
        >
          {saving ? 'Saving...' : 'Save & Continue'}
        </Button>
      </div>
    </form>
  );
}